import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Facebook, Link2, Check, RefreshCw, Loader2, ImageOff, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { api, errorMessage, openFacebookShare } from "../../lib/api";
import { basarShareUrl } from "../../lib/basar";
import { useLang } from "../../context/LanguageContext";

/**
 * Delingskort (1080×1080) i dashbordet: førehandsvising, nedlasting, «Del på Facebook» og kopiering av delingslenka.
 * Biletet blir henta med innlogging (òg for kladd) og laga på nytt med «Lag på nytt». Delingslenka (/api/share/basar/:id)
 * gjev Facebook/Messenger kortet som automatisk førehandsvising.
 */
export const ShareCardPanel = ({ basar }) => {
  const { t } = useLang();
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const blobRef = useRef(null);
  const version = basar?.updated_at || "";

  const load = useCallback(
    async (refresh = false) => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await api.get(`/my/basar/${basar.id}/share-card.jpg`, { responseType: "blob", params: refresh ? { refresh: 1 } : {} });
        if (blobRef.current) URL.revokeObjectURL(blobRef.current);
        blobRef.current = URL.createObjectURL(res.data);
        setSrc(blobRef.current);
        if (refresh) toast.success(t("Delingskortet er laget på nytt"));
      } catch (err) {
        setFailed(true);
        if (refresh) toast.error(errorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [basar?.id, t]
  );

  useEffect(() => {
    if (basar?.id) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basar?.id, version]);

  useEffect(() => () => blobRef.current && URL.revokeObjectURL(blobRef.current), []);

  const shareUrl = basarShareUrl(basar.id);
  const isPublic = basar.status !== "draft";

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = `delingskort-${(basar.name || "basar").toLowerCase().replace(/[^a-z0-9æøå]+/gi, "-").replace(/^-|-$/g, "")}.jpg`;
    a.click();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("Delingslenken er kopiert"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("Kunne ikke kopiere – merk lenken og kopier manuelt"));
    }
  };

  return (
    <Card className="border-border" data-testid="basar-share-card-panel">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-heading font-semibold inline-flex items-center gap-2"><Share2 className="w-4 h-4 text-[hsl(var(--warning))]" /> {t("Delingskort")}</p>
            <p className="text-sm text-muted-foreground">{t("Kvadratisk bilde (1080×1080) med de største premiene, pris per lodd og QR til basarsiden – klart for Facebook og Instagram.")}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => load(true)} disabled={loading} aria-label={t("Lag på nytt")} title={t("Lag på nytt")} data-testid="basar-share-card-refresh-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <div className="rounded-xl overflow-hidden border border-border bg-muted/40 aspect-square max-w-[360px]" data-testid="basar-share-card-preview">
          {loading && !src ? (
            <Skeleton className="w-full h-full" />
          ) : failed ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm p-6 text-center" data-testid="basar-share-card-error">
              <ImageOff className="w-6 h-6" />
              {t("Kunne ikke lage delingskortet.")}
              <Button variant="outline" size="sm" onClick={() => load(true)} data-testid="basar-share-card-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button>
            </div>
          ) : (
            <img src={src} alt={t("Delingskort for {name}", { name: basar.name })} className={`w-full h-full object-cover transition-opacity duration-200 ${loading ? "opacity-60" : "opacity-100"}`} data-testid="basar-share-card-image" />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={download} disabled={!src || failed} data-testid="basar-share-card-download-button"><Download className="w-4 h-4 mr-1.5" /> {t("Last ned bilde")}</Button>
          <Button size="sm" onClick={() => openFacebookShare(shareUrl)} disabled={!isPublic} title={!isPublic ? t("Åpne loddsalget først – kladder kan ikke deles") : undefined} data-testid="basar-share-facebook-button"><Facebook className="w-4 h-4 mr-1.5" /> {t("Del på Facebook")}</Button>
          <Button variant="outline" size="sm" onClick={copyLink} disabled={!isPublic} data-testid="basar-share-copy-link-button">{copied ? <Check className="w-4 h-4 mr-1.5 text-[hsl(var(--success))]" /> : <Link2 className="w-4 h-4 mr-1.5" />} {t("Kopier delingslenke")}</Button>
        </div>
        <p className="text-xs text-muted-foreground">{isPublic ? t("Delingslenken viser dette kortet som forhåndsvisning når den deles på Facebook, Messenger eller i gruppechatten.") : t("Når loddsalget er åpnet, kan du dele lenken – kortet blir da forhåndsvisningen på Facebook.")}</p>
      </CardContent>
    </Card>
  );
};
